import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { ChevronDown, Menu } from 'lucide-react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { useDocumentationMenu } from "./nav-links"
import LanguageSwitcher from "@/components/language-switcher"
import { ThemeSwitch } from "@/components/theme-switch"
import { useNavigate } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { tokens } from "@/locales/translations/tokens"

const NavBarLandingPage = () => {
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)
  const { t } = useTranslation();
  const documentationMenu = useDocumentationMenu();

  const handleNavigate = (path?: string) => {
    navigate({ to: path || "/" })
  }

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled)
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [scrolled])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const menuGroup = (name: string, items: any[]) => (
    <DropdownMenu>
      <DropdownMenuTrigger className="group flex items-center gap-1 text-sm font-medium text-muted-foreground outline-none transition-colors hover:text-foreground">
        <span className="relative">
          {name}
          <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-primary transition-all duration-300 group-hover:w-full" />
        </span>
        <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        className="w-[280px] overflow-hidden rounded-xl p-2"
        asChild
      >
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {items.map((item, idx) => (
            <DropdownMenuItem
              key={idx}
              onClick={() => handleNavigate(item.link)}
              className="flex cursor-pointer items-start gap-3 rounded-lg p-3 transition-colors hover:bg-accent"
            >
              <div className="mt-0.5 rounded-md bg-primary/10 p-1.5 text-primary">
                <item.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium">{item.label}</p>
                <p className="text-xs text-muted-foreground">{item.description}</p>
              </div>
            </DropdownMenuItem>
          ))}
        </motion.div>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 border-b transition-all duration-300 mr-0 ${
        scrolled
          ? "border-border/40 bg-background/70 backdrop-blur-lg"
          : "border-transparent bg-background/70 backdrop-blur-md"
      }`}
    >
      <div className="container flex h-16 items-center justify-between px-4 md:px-6">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="flex items-center"
        >
          <span onClick={() => handleNavigate()} className="cursor-pointer">
            <img
              src="/brand/alan/favicon/a-workstation-light-lm.png"
              alt="Logo"
              className="h-8 dark:hidden"
            />
            <img
              src="/brand/alan/favicon/a-workstation-dark-lm.png"
              alt="Logo Dark"
              className="hidden h-8 dark:block"
            />
          </span>
        </motion.div>

        {/* Desktop Menu */}
        <div className="flex items-center">
          <nav className="hidden items-center gap-8 md:flex">
            {/* {menuGroup("Features", ProductsMenu)} */}
            {menuGroup(t(tokens.landing.resources), documentationMenu)}
            {/* {menuGroup("Blog", pagesBlog)} */}
          </nav>

          {/* Actions */}
          <div className="hidden items-center gap-4 md:flex">
            <Button
              variant="ghost"
              onClick={() => handleNavigate("/sign-in")}
              className="text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              {t(tokens.landing.login)}
            </Button>
            <LanguageSwitcher />
            <ThemeSwitch />
          </div>
        </div>

      </div>
      
      {/* Mobile */}
      <div className="flex items-center gap-2 md:hidden">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full absolute top-3 right-5">
              <Menu className="h-5 w-5" /> {/* ícone ☰ */}
            </Button>
          </SheetTrigger>

          <SheetContent side="right" className="w-full max-w-[350px] p-0">
            <SheetHeader className="border-b p-4">
              <SheetTitle className="text-left">Menu</SheetTitle>
            </SheetHeader>

            <div className="flex flex-col gap-4 p-4">
              <div className="space-y-1">
                <h4 className="px-2 py-1 text-sm font-semibold text-muted-foreground">
                  {t(tokens.landing.resources)}
                </h4>
                {documentationMenu.map((item, idx) => (
                  <SheetClose asChild key={idx}>
                    <Button
                      variant="ghost"
                      className="flex w-full justify-start gap-2 rounded-lg px-2"
                      onClick={() => handleNavigate(item.link)}
                    >
                      <item.icon className="h-4 w-4 text-primary" />
                      <span>{item.label}</span>
                    </Button>
                  </SheetClose>
                ))}
              </div>

              <Separator />
              
              <div className="pt-4 flex flex-col gap-4">
                {/* Login */}
                <Button
                  variant="default"
                  className="w-full justify-start text-center"
                  onClick={() => handleNavigate('/sign-in')}
                >
                  <p className="text-center w-full"> 
                    {t(tokens.landing.login)}
                  </p>
                </Button>

                {/* Idioma + Tema */}
                <div className="flex items-center justify-between">
                  <LanguageSwitcher />
                  <ThemeSwitch />
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Mobile Search Bar */}
      {/* <AnimatePresence>
        {mobileSearchVisible && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-border/30 bg-background/80 backdrop-blur-lg md:hidden"
          >
            <div className="container p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search for anything..."
                  className="w-full rounded-full pl-10 pr-10"
                  autoFocus
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full"
                  onClick={() => setMobileSearchVisible(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence> */}
    </header>
  )
}

export default NavBarLandingPage

